import {
  Button as AriaButton,
  type ButtonProps as AriaButtonProps,
} from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { tv, type VariantProps } from "tailwind-variants";
import { DenovoIcon, IconName } from "../icons";

const buttonVariants = tv({
  base: "inline-flex items-center justify-center gap-2 rounded-lg border-2 border-transparent font-semibold transition-colors duration-base ease-out cursor-pointer outline-none data-focus-visible:outline-2 data-focus-visible:outline-offset-2 data-focus-visible:outline-borderFocus data-disabled:opacity-50 data-disabled:cursor-not-allowed",
  variants: {
    variant: {
      primary:
        "bg-backgroundBrand text-contentOnBrand border-transparent data-hovered:bg-b700! data-pressed:bg-b700!",
      secondary:
        "bg-transparent text-contentPrimary border-borderPrimary data-hovered:bg-n100! data-hovered:border-borderBrand dark:data-hovered:bg-n800! data-pressed:bg-n200! dark:data-pressed:bg-n700!",
      tertiary:
        "bg-backgroundSecondary text-contentPrimary border-transparent data-hovered:bg-n200! dark:data-hovered:bg-[#12273F]! data-pressed:bg-n300! dark:data-pressed:bg-n800!",
      transparent:
        "bg-transparent text-contentSecondary border-transparent data-hovered:bg-n100! data-hovered:text-contentPrimary dark:data-hovered:bg-n800! data-pressed:bg-n200! dark:data-pressed:bg-n700!",
      danger:
        "bg-r500 text-n0 border-transparent data-hovered:bg-r600! data-pressed:bg-r700!",
    },
    size: {
      small: "h-8 px-3 text-pl",
      medium: "h-10 px-4 text-pl",
      large: "h-[46px] px-[22px] text-pxl",
    },
    fullWidth: {
      true: "w-full",
      false: "w-auto",
    },
  },
  defaultVariants: {
    variant: "primary",
    size: "medium",
    fullWidth: false,
  },
});

export interface ButtonProps
  extends AriaButtonProps, VariantProps<typeof buttonVariants> {
  rightIcon?: IconName;
  leftIcon?: IconName;
  children?: React.ReactNode | string;
  className?: string;
  loading?: boolean;
}

export const Button = ({
  variant,
  size,
  fullWidth,
  className,
  leftIcon,
  rightIcon,
  children,
  loading = false,
  isDisabled,
  ...props
}: ButtonProps) => {
  const isButtonDisabled = isDisabled || loading;

  return (
    <AriaButton
      {...props}
      isDisabled={isButtonDisabled}
      className={twMerge(
        buttonVariants({ variant, size, fullWidth }),
        className,
      )}>
      {loading ? (
        <>
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
          {children && <span className="ms-1">{children}</span>}
        </>
      ) : (
        <>
          {leftIcon && (
            <DenovoIcon
              icon={leftIcon}
              className="w-4 h-4 shrink-0 transform-gpu"
            />
          )}
          {children}
          {rightIcon && (
            <DenovoIcon
              icon={rightIcon}
              className="w-4 h-4 shrink-0 transform-gpu"
            />
          )}
        </>
      )}
    </AriaButton>
  );
};
